import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarCheck2 } from "lucide-react"
import { useMemo } from "react"

import { ActiveAbsencesCard } from "@/components/absence-review/active-absences-card"
import { PendingRequestsCard } from "@/components/absence-review/pending-requests-card"
import { ReviewFilterCard } from "@/components/absence-review/review-filter-card"
import { ReviewMessageAlert } from "@/components/absence-review/review-message-alert"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Card } from "@/components/ui/card"
import { DashboardSkeleton } from "@/components/ui/page-skeletons"
import { absenceTrackingApi } from "@/lib/absence-tracking-api"
import { createAbsenceReviewUiStore } from "@/lib/stores/absence-review-ui-store"
import { useAbsenceReviewRouteStore } from "@/lib/stores/absence-review-ui-store"

const adminAbsenceOverviewQueryKey = ["absence-tracking", "admin"] as const

export const Route = createFileRoute("/_authed/_admin/absence-review")({
  beforeLoad: () => ({
    absenceReviewStore: createAbsenceReviewUiStore(),
  }),
  component: AdminAbsencesPage,
})

function AdminAbsencesPage() {
  const store = useAbsenceReviewRouteStore()
  const overviewQuery = useQuery({
    queryKey: adminAbsenceOverviewQueryKey,
    queryFn: absenceTrackingApi.getAdminOverview,
  })

  const metrics = useMemo(() => {
    return {
      active: overviewQuery.data?.activeAbsences.length ?? 0,
      pending: overviewQuery.data?.pendingRequests.length ?? 0,
      total:
        (overviewQuery.data?.activeAbsences.length ?? 0) +
        (overviewQuery.data?.pendingRequests.length ?? 0),
    }
  }, [overviewQuery.data])

  if (overviewQuery.isLoading) {
    return <DashboardSkeleton />
  }

  if (overviewQuery.isError) {
    return (
      <StatusAlert tone="danger">
        <div className="flex items-center justify-between gap-3">
          <span>Failed to load absence review data.</span>
          <button
            type="button"
            className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
            onClick={() => void overviewQuery.refetch()}
          >
            Retry
          </button>
        </div>
      </StatusAlert>
    )
  }

  return (
    <div className="grid gap-4 md:gap-5">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          headerClassName="gap-4 md:gap-5"
          kicker={
            <SectionKicker icon={<CalendarCheck2 />}>
              Officer Review
            </SectionKicker>
          }
          title="Keep absence coverage readable for the whole guild"
          titleClassName="text-4xl leading-none font-semibold md:text-5xl"
          description="Review pending submissions, confirm upcoming time away, and maintain an up-to-date view of guild availability."
          descriptionClassName="max-w-3xl text-base leading-7"
          actions={
            <RefreshButton
              isFetching={overviewQuery.isFetching}
              onClick={() => void overviewQuery.refetch()}
            />
          }
          leftPanelClassName="gap-3"
          metrics={
            <>
              <MetricTile label="Active" value={String(metrics.active)} />
              <MetricTile label="Pending" value={String(metrics.pending)} />
              <MetricTile label="Open" value={String(metrics.total)} />
            </>
          }
        />
      </Card>

      <ReviewMessageAlert store={store} />
      <ReviewFilterCard store={store} />

      <PendingRequestsCard
        store={store}
        pendingRequests={overviewQuery.data?.pendingRequests ?? []}
        reviewRequired={overviewQuery.data?.reviewRequired ?? false}
      />

      <ActiveAbsencesCard
        store={store}
        activeAbsences={overviewQuery.data?.activeAbsences ?? []}
      />
    </div>
  )
}
